import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, ChevronRight, SkipForward } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS_DEFAULT = [
  {
    target: '[data-testid="nav-dashboard"]',
    title: 'Witaj w CRM Trendhomes!',
    text: 'To Twój dashboard — tu widzisz najważniejsze wskaźniki.',
  },
  {
    target: '[data-testid="nav-projects"]',
    title: 'Projekty',
    text: 'Tu znajdziesz swoje projekty. Kliknij aby zobaczyć listę.',
  },
  {
    target: '[data-testid="nav-kanban"]',
    title: 'Kanban',
    text: 'Kanban to tablica — przeciągaj karty aby zmieniać statusy.',
  },
  {
    target: '[data-onboarding="search-trigger"]',
    title: 'Szybkie wyszukiwanie',
    text: 'Użyj Ctrl+K aby szybko znaleźć dowolny projekt.',
  },
  {
    target: '[data-testid="nav-help"]',
    title: 'Gotowe!',
    text: 'Możesz zacząć pracę. Pomoc znajdziesz zawsze w menu bocznym.',
    isLast: true,
  },
];

const STEPS_B2B = [
  {
    target: '[data-testid="nav-dashboard"]',
    title: 'Witaj!',
    text: 'To Twój osobisty dashboard — tu widzisz swoją prowizję i realizację planu.',
  },
  {
    target: '[data-testid="nav-projects"]',
    title: 'Twoje projekty',
    text: 'Tu są Twoje projekty. Widzisz tylko swoje zlecenia.',
  },
  {
    target: '[data-testid="add-project-btn"]',
    title: 'Nowy Lead',
    text: 'Kliknij Nowy Lead aby dodać nowe zlecenie z terenu.',
  },
  {
    target: '[data-testid="nav-dashboard"]',
    title: 'Wartość oferty',
    text: 'Na karcie projektu widzisz wartość oferty i szacowaną prowizję.',
  },
  {
    target: '[data-testid="nav-help"]',
    title: 'Gotowe!',
    text: 'Powodzenia w sprzedaży!',
    isLast: true,
  },
];

export const OnboardingTour = () => {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [highlight, setHighlight] = useState(null);

  const steps = user?.role === 'handlowiec_b2b' ? STEPS_B2B : STEPS_DEFAULT;

  useEffect(() => {
    if (user && !user.onboarding_completed) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const updateHighlight = useCallback(() => {
    if (!visible || step >= steps.length) return;
    const el = document.querySelector(steps[step].target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setHighlight({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      setHighlight(null);
    }
  }, [step, visible, steps]);

  useEffect(() => {
    updateHighlight();
    window.addEventListener('resize', updateHighlight);
    return () => window.removeEventListener('resize', updateHighlight);
  }, [updateHighlight]);

  const finish = async () => {
    setVisible(false);
    try {
      await axios.patch(`${API_URL}/users/onboarding`);
      if (setUser) {
        setUser(prev => {
          const updated = { ...prev, onboarding_completed: true };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
      }
    } catch {}
  };

  const next = () => {
    if (step >= steps.length - 1) {
      finish();
    } else {
      setStep(s => s + 1);
    }
  };

  if (!visible || !user) return null;

  const current = steps[step];

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-overlay">
      {/* Dark overlay with cutout */}
      <div className="absolute inset-0 bg-black/60" onClick={(e) => e.stopPropagation()} />

      {/* Highlight box */}
      {highlight && (
        <div className="absolute rounded-lg ring-2 ring-[#FF6B35] ring-offset-2 ring-offset-transparent bg-transparent z-[10000] transition-all duration-300 pointer-events-none"
          style={{
            top: highlight.top - 4, left: highlight.left - 4,
            width: highlight.width + 8, height: highlight.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          }}
        />
      )}

      {/* Tooltip bubble */}
      <div className="absolute z-[10001] bg-white rounded-xl shadow-2xl p-5 w-[340px] transition-all duration-300"
        data-testid="onboarding-bubble"
        style={{
          top: highlight ? Math.min(highlight.top + highlight.height + 16, window.innerHeight - 200) : '50%',
          left: highlight ? Math.max(16, Math.min(highlight.left, window.innerWidth - 360)) : '50%',
          transform: highlight ? 'none' : 'translate(-50%, -50%)',
        }}>
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1.5">
            {steps.map((s, i) => (
              <div key={s.target || `step-${i}`} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-[#FF6B35]' : i < step ? 'w-3 bg-[#FF6B35]/50' : 'w-3 bg-slate-200'}`} />
            ))}
          </div>
          <span className="text-xs text-[var(--crm-text-secondary)] font-medium">{step + 1}/{steps.length}</span>
        </div>

        <h3 className="text-slate-900 font-bold text-base mb-1">{current.title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed mb-4">{current.text}</p>

        <div className="flex items-center justify-between">
          <button onClick={finish} className="text-[var(--crm-text-secondary)] hover:text-slate-600 text-sm flex items-center gap-1 transition-colors"
            data-testid="onboarding-skip">
            <SkipForward className="w-3.5 h-3.5" />Pomiń
          </button>
          <button onClick={next}
            data-testid="onboarding-next"
            className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors">
            {current.isLast ? 'Zamknij' : 'Dalej'}
            {!current.isLast && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};
